<template>
  <v-card
    class="overflow-hidden shop-management-setting pb-0"
    elevation="0"
    min-width="1170"
  >
    <v-app-bar
      class="shop-management-setting__header"
      color="white"
      height="93"
      elevate-on-scroll
      scroll-target="#shop-management-setting__form"
    >
      <Breadcrumbs
        :breadcrumbs="breadcrumbs"
        class="ma-0"
      />

      <v-btn
        class="text-body-2 ml-auto mr-3 cancel-btn"
        width="137"
        height="32"
        outlined
        depressed
        color="neutral-045"
        @click="openCheckModal('cancel')"
      >
        {{ $t('CANCEL') }}
      </v-btn>
      <v-btn
        class="text-body-2"
        width="137"
        height="32"
        dark
        depressed
        color="primary-001"
        :loading="isSubmitLoading"
        v-on="{click: currentShopLocationCode ? updateShop : createShop}"
      >
        {{ currentShopLocationCode? $t('SAVE'):$t('CREATE') }}
      </v-btn>
    </v-app-bar>

    <v-sheet
      id="shop-management-setting__form"
      class="overflow-y-auto mt-3 shop-management-setting__form"
      width="100%"
      max-height="calc(100vh - 105px)"
    >
      <v-form
        ref="form"
        v-model="formValid"
        lazy-validation
      >
        <div style="width: 423px;">
          <!-- 基本設定 -->
          <div class="text-subtitle-4 neutral-040--text mb-3">
            {{ $t('SHOP_MANAGEMENT.BASIC_SETTING') }}
          </div>

          <!-- 分店名稱 -->
          <Input
            v-model="currentShopLocation.locationName"
            :label="$t('SHOP_MANAGEMENT.SHOP_NAME')"
            :placeholder="$t('SHOP_MANAGEMENT.PLEASE_ENTER_SHOP_NAME')"
            class="mb-2"
            style="margin-bottom: 6px;"
            required
            :rules="[(value) => !!value || $t('THIS_FIELD_IS_REQUIRED')]"
          />

          <!-- 分店編號 -->
          <Input
            v-model="currentShopLocation.locationCode"
            :label="$t('SHOP_MANAGEMENT.SHOP_LOCATION_CODE')"
            :placeholder="$t('SHOP_MANAGEMENT.PLEASE_ENTER_SHOP_LOCATION_CODE')"
            class="mb-2"
            required
            :rules="[(value) => !!value || $t('THIS_FIELD_IS_REQUIRED')]"
          />

          <!-- 區域 -->
          <CategoriesCombobox
            v-model="currentShopLocation.categories"
            :label="`${$t('SHOP_MANAGEMENT.REGION')} (${$t('OPTIONAL')})`"
            :placeholder="$t('SHOP_MANAGEMENT.PLEASE_ENTER_REGION')"
            class="mb-8"
          />

          <!-- 綁定訊息設定 -->
          <div class="text-subtitle-4 neutral-040--text mb-3 d-flex align-center">
            {{ $t('SHOP_MANAGEMENT.BINDING_REPLY_SETTING') }}
            <v-btn
              class="ml-1"
              icon
              small
              target="_blank"
              :href="omnichatDocs + 'features/omo-she-ding/omo-bang-ding-she-ding#bang-ding-dian-yuan-cheng-gong-xun-xi'"
            >
              <v-icon color="neutral-044">
                mdi-help-circle
              </v-icon>
            </v-btn>
          </div>
          <v-radio-group
            v-model="currentShopLocation.bindSuccessReplyType"
          >
            <template v-slot:label>
              <div>{{ $t('SHOP_MANAGEMENT.BIND_SUCCESS_REPLY_TYPE') }}</div>
            </template>
            <div class="d-flex">
              <v-radio
                :value="CONST.BINDING_SUCCESS_REPLY_TYPE_DEFAULT"
                class="custom-radio"
                on-icon="$vuetify.icons.radioIcon"
                off-icon="$vuetify.icons.radioFalseIcon"
              >
                <template v-slot:label>
                  <div class="text-body-3">
                    {{ $t('SHOP_MANAGEMENT.BINDING_SUCCESS_REPLY_TYPE_TEAM_DEFAULT') }}
                  </div>
                </template>
              </v-radio>

              <router-link
                class="text-body-2 primary-001--text"
                style="text-decoration: none;margin-left: 6px;padding-top: 2px;"
                to="/omo-binding-setting#BindingSuccessMessage"
                target="_blank"
              >
                {{ $t('SHOP_MANAGEMENT.LOOK_UP_SETTING') }}
              </router-link>
            </div>

            <v-radio
              :value="CONST.BINDING_SUCCESS_REPLY_TYPE_TEXT"
              class="custom-radio"
              on-icon="$vuetify.icons.radioIcon"
              off-icon="$vuetify.icons.radioFalseIcon"
            >
              <template v-slot:label>
                <div class="text-body-3">
                  {{ $t('SHOP_MANAGEMENT.BINDING_SUCCESS_REPLY_TYPE_SHOP_TEXT') }}
                </div>
              </template>
            </v-radio>

            <div
              class="textarea-tool ml-14"
              v-show="currentShopLocation.bindSuccessReplyType === CONST.BINDING_SUCCESS_REPLY_TYPE_TEXT"
            >
              <TextareaEditor
                v-model="currentShopLocation.bindingSuccessReplyBody"
                rows="2"
                counter
                maxlength="1024"
                textarea-id="bindSuccessReplyTypeText"
                :placeholder="$t('WHATSAPP_TEMPLATE.PLEASE_ADD_BODY_TEXT')"
                :variables="[
                  {
                    title: this.$t('SHOP_MANAGEMENT.EMPLOYEE_NAME'),
                    name: 'employeeName'
                  },
                  {
                    title: this.$t('SHOP_MANAGEMENT.SHOP_NAME'),
                    name: 'locationName'
                  }
                ]"
              />
            </div>

            <v-radio
              :value="CONST.BINDING_SUCCESS_REPLY_TYPE_NO_MSG"
              class="custom-radio"
              on-icon="$vuetify.icons.radioIcon"
              off-icon="$vuetify.icons.radioFalseIcon"
            >
              <template v-slot:label>
                <div class="text-body-3">
                  {{ $t('SHOP_MANAGEMENT.BINDING_SUCCESS_REPLY_TYPE_NO_MSG') }}
                </div>
              </template>
            </v-radio>
          </v-radio-group>

          <!-- 綁定成功附加訊息 -->
          <v-radio-group
            v-model="currentShopLocation.bindSuccessExtraReplyType"
          >
            <template v-slot:label>
              <div>{{ $t('SHOP_MANAGEMENT.BIND_SUCCESS_EXTRA_REPLY_TYPE') }}</div>
            </template>
            <div class="d-flex">
              <v-radio
                :value="CONST.BINDING_SUCCESS_EXTRA_REPLY_TYPE_DEFAULT"
                class="custom-radio"
                on-icon="$vuetify.icons.radioIcon"
                off-icon="$vuetify.icons.radioFalseIcon"
              >
                <template v-slot:label>
                  <div class="text-body-3">
                    {{ $t('SHOP_MANAGEMENT.BINDING_SUCCESS_REPLY_TYPE_TEAM_DEFAULT') }}
                  </div>
                </template>
              </v-radio>
              <router-link
                class="text-body-2 primary-001--text"
                style="text-decoration: none;margin-left: 6px;padding-top: 2px;"
                to="/omo-binding-setting#BindingSuccessMessage"
                target="_blank"
              >
                {{ $t('SHOP_MANAGEMENT.LOOK_UP_SETTING') }}
              </router-link>
            </div>

            <v-radio
              :value="CONST.BINDING_SUCCESS_EXTRA_REPLY_TYPE_TEXT"
              class="custom-radio"
              on-icon="$vuetify.icons.radioIcon"
              off-icon="$vuetify.icons.radioFalseIcon"
            >
              <template v-slot:label>
                <div class="text-body-3">
                  {{ $t('SHOP_MANAGEMENT.BINDING_SUCCESS_REPLY_TYPE_SHOP_TEXT') }}
                </div>
              </template>
            </v-radio>

            <div
              class="textarea-tool  ml-14"
              v-show="currentShopLocation.bindSuccessExtraReplyType === CONST.BINDING_SUCCESS_EXTRA_REPLY_TYPE_TEXT"
            >
              <TextareaEditor
                v-model="bindingSuccessExtraReplyMessage"
                rows="2"
                counter
                maxlength="1024"
                textarea-id="bindingSuccessExtraReplyMessage"
                :placeholder="$t('WHATSAPP_TEMPLATE.PLEASE_ADD_BODY_TEXT')"
                :variables="[
                  {
                    title: this.$t('SHOP_MANAGEMENT.EMPLOYEE_NAME'),
                    name: 'employeeName'
                  },
                  {
                    title: this.$t('SHOP_MANAGEMENT.SHOP_NAME'),
                    name: 'locationName'
                  }
                ]"
              />
            </div>

            <v-radio
              :value="CONST.BINDING_SUCCESS_EXTRA_REPLY_TYPE_CHATBOT"
              class="custom-radio"
              on-icon="$vuetify.icons.radioIcon"
              off-icon="$vuetify.icons.radioFalseIcon"
            >
              <template v-slot:label>
                <div class="text-body-3">
                  {{ $t('SHOP_MANAGEMENT.BINDING_SUCCESS_EXTRA_REPLY_TYPE_STORE_CHATBOT') }}
                </div>
              </template>
            </v-radio>

            <div
              v-if="currentShopLocation.bindSuccessExtraReplyType === CONST.BINDING_SUCCESS_EXTRA_REPLY_TYPE_CHATBOT"
              class="ml-14"
            >
              <Select
                class="mb-2"
                :placeholder="$t('SELECT_BOT')"
                v-model="currentShopLocation.bindingSuccessExtraReplyChatbotId"
                :items="chatbotList"
                item-text="text"
                item-value="value"
                solo
                flat
                required
                :rules="[(value) => !!value || $t('THIS_FIELD_IS_REQUIRED')]"
              />
              <Select
                class="mb-2"
                :placeholder="$t('SELECT_BOT_MESSAGE_BLOCK')"
                v-model="bindingSuccessExtraReplyBlockId"
                :items="currentSelectedMessageBlocks"
                item-text="text"
                item-value="value"
                solo
                flat
                required
                :rules="[(value) => !!value || $t('THIS_FIELD_IS_REQUIRED')]"
              />
            </div>

            <v-radio
              :value="CONST.BINDING_SUCCESS_EXTRA_REPLY_TYPE_NO_MSG"
              class="custom-radio"
              on-icon="$vuetify.icons.radioIcon"
              off-icon="$vuetify.icons.radioFalseIcon"
            >
              <template v-slot:label>
                <div class="text-body-3">
                  {{ $t('SHOP_MANAGEMENT.BINDING_SUCCESS_REPLY_TYPE_NO_MSG') }}
                </div>
              </template>
            </v-radio>
          </v-radio-group>
        </div>
      </v-form>
    </v-sheet>
    <Modal ref="checkModal">
      <template v-slot:actions>
        <v-card-actions class="pa-0 pt-8">
          <v-spacer />

          <template v-if="modalType === 'cancel'">
            <v-btn
              color="neutral-045"
              outlined
              @click="$refs.checkModal.closeModal();"
              class="text-body-2 cancel-btn"
            >
              {{ $t('CANCEL') }}
            </v-btn>
            <v-btn
              color="danger-022"
              outlined
              @click="closeModalAndLeave"
              class="text-body-2 ml-3"
            >
              {{ $t('LEAVE') }}
            </v-btn>
          </template>
          <v-btn
            v-else
            color="primary-001"
            dark
            depressed
            @click="closeModalAndLeave"
            class="text-body-2 ml-3"
          >
            {{ $t('CONFIRM') }}
          </v-btn>
        </v-card-actions>
      </template>
    </Modal>
    <Notification
      ref="notification"
    />
  </v-card>
</template>

<script>
import Breadcrumbs from '@/components/BreadCrumbs.vue';
import Input from '@/components/form/Input.vue';
import Modal from '@/components/Modal.vue';
import shopApi from '@/restapis/shopManagement';
import botApi from '@/restapis/bots';
import Select from '@/components/form/Select.vue';
import Notification from '@/components/Notification.vue';

import TextareaEditor from '@/components/form/TextareaEditor.vue';
import CategoriesCombobox from './components/CategoriesCombobox.vue';

export default {
  name: 'ShopManagementSetting',
  metaInfo: {
    title: 'Shop Management Setting - Omnichat Admin Panel'
  },
  components: {
    Breadcrumbs,
    Input,
    CategoriesCombobox,
    TextareaEditor,
    Modal,
    Select,
    Notification
  },
  data() {
    return {
      CONST: {
        BINDING_SUCCESS_REPLY_TYPE_DEFAULT: 0,
        BINDING_SUCCESS_REPLY_TYPE_TEXT: 1,
        BINDING_SUCCESS_REPLY_TYPE_NO_MSG: 2,
        BINDING_SUCCESS_EXTRA_REPLY_TYPE_DEFAULT: 0,
        BINDING_SUCCESS_EXTRA_REPLY_TYPE_TEXT: 1,
        BINDING_SUCCESS_EXTRA_REPLY_TYPE_CHATBOT: 2,
        BINDING_SUCCESS_EXTRA_REPLY_TYPE_NO_MSG: 3
      },
      currentShopLocationCode: '',
      currentShopLocation: {
        locationCode: '',
        locationName: '',
        bindSuccessExtraReplyType: 0,
        bindSuccessReplyType: 0,
        bindingSuccessExtraReplyBody: null,
        bindingSuccessExtraReplyChatbotId: null,
        bindingSuccessReplyBody: '',
        categories: []
      },
      bindingSuccessExtraReplyMessage: '',
      bindingSuccessExtraReplyBlockId: null,
      isSubmitLoading: false,
      categorySearch: '',
      modalType: '',
      chatbotList: [],
      currentSelectedMessageBlocks: [],
      formValid: true,
      omoBindingSettingUrl: `${process.env.VUE_APP_CHAT_ADMIN_URL}/omo-binding-setting.html`,
      omnichatDocs: this.$t('OMNICHAT_DOCS')
    };
  },
  async created() {
    this.$store.commit('loader/showLoader', true);
    await this.fetchAllBotsLists();
    if (this.$route.params.id) {
      this.currentShopLocationCode = this.$route.params.id;
      try {
        const { data } = await shopApi.getShopInfo(this.currentShopLocationCode);
        const shopInfo = data.data;
        this.currentShopLocation = {
          locationCode: shopInfo.locationCode,
          locationName: shopInfo.locationName,
          bindSuccessExtraReplyType: shopInfo.bindSuccessExtraReplyType,
          bindSuccessReplyType: shopInfo.bindSuccessReplyType,
          bindingSuccessExtraReplyBody: shopInfo.bindingSuccessExtraReplyBody,
          bindingSuccessExtraReplyChatbotId: shopInfo.bindingSuccessExtraReplyChatbotId,
          bindingSuccessReplyBody: shopInfo.bindingSuccessReplyBody,
          categories: shopInfo.categories
        };

        if (shopInfo.bindSuccessExtraReplyType === this.CONST.BINDING_SUCCESS_EXTRA_REPLY_TYPE_CHATBOT) {
          this.bindingSuccessExtraReplyBlockId = shopInfo.bindingSuccessExtraReplyBody;
        } else if (shopInfo.bindSuccessExtraReplyType === this.CONST.BINDING_SUCCESS_EXTRA_REPLY_TYPE_TEXT) {
          this.bindingSuccessExtraReplyMessage = shopInfo.bindingSuccessExtraReplyBody;
        }
      } catch (e) {
        const notificationObj = {
          title: this.$t('SHOP_MANAGEMENT.GET_SHOP_INFO_FAILED'),
          status: 'error'
        };
        this.$refs.notification.openNotification(notificationObj);
      }
      setTimeout(() => {
        this.$store.commit('loader/showLoader', false);
      }, 300);
    } else {
      setTimeout(() => {
        this.$store.commit('loader/showLoader', false);
      }, 300);
    }
  },
  computed: {
    breadcrumbs() {
      return [
        {
          text: this.$t('SHOP_MANAGEMENT.TITLE'),
          disabled: false,
          to: { name: 'shopManagementDashboard' }
        },
        {
          text: this.currentShopLocationCode
            ? this.$t('SHOP_MANAGEMENT.EDIT_SHOP')
            : this.$t('SHOP_MANAGEMENT.CREATE_SHOP'),
          disabled: true,
          href: ''
        }
      ];
    }
  },
  watch: {
    'currentShopLocation.bindingSuccessExtraReplyChatbotId': async function (chatbotId) {
      const { data } = await botApi.getMessageBlocks(chatbotId);
      this.currentSelectedMessageBlocks = data.map((item) => ({
        text: item.name,
        value: item.id
      }));
    }
  },
  methods: {
    async createShop() {
      if (!this.checkFormValidate()) return;
      this.isSubmitLoading = true;
      try {
        // set bindingExtraReplyBody by bindSuccessExtraReplyType
        this.currentShopLocation.bindingSuccessExtraReplyBody = this.currentShopLocation.bindSuccessExtraReplyType === this.CONST.BINDING_SUCCESS_EXTRA_REPLY_TYPE_CHATBOT
          ? this.bindingSuccessExtraReplyBlockId
          : this.bindingSuccessExtraReplyMessage;

        await shopApi.createShops([this.currentShopLocation]);
        this.isSubmitLoading = false;
        this.openCheckModal('create');
      } catch (e) {
        this.isSubmitLoading = false;
        const notificationObj = {
          title: this.$t('SHOP_MANAGEMENT.CREATE_FAILED'),
          status: 'error'
        };
        this.$refs.notification.openNotification(notificationObj);
      }
    },
    async updateShop() {
      if (!this.checkFormValidate()) return;
      this.isSubmitLoading = true;
      try {
        // set bindingExtraReplyBody by bindSuccessExtraReplyType
        this.currentShopLocation.bindingSuccessExtraReplyBody = this.currentShopLocation.bindSuccessExtraReplyType === this.CONST.BINDING_SUCCESS_EXTRA_REPLY_TYPE_CHATBOT
          ? this.bindingSuccessExtraReplyBlockId
          : this.bindingSuccessExtraReplyMessage;

        await shopApi.editShop(this.currentShopLocationCode, this.currentShopLocation);
        this.isSubmitLoading = false;
        this.openCheckModal('update');
      } catch (e) {
        this.isSubmitLoading = false;
        const notificationObj = {
          title: this.$t('SHOP_MANAGEMENT.UPDATE_FAILED'),
          status: 'error'
        };
        this.$refs.notification.openNotification(notificationObj);
      }
    },
    openCheckModal(modalType) {
      this.modalType = modalType;
      let title = '';
      let content = '';
      switch (modalType) {
        case 'cancel':
          title = this.$t('ARE_YOU_SURE_TO_LEAVE_THE_PAGE');
          content = this.$t('LEAVE_PAGE_DATA_WILL_BE_LOST');
          break;
        case 'update':
          title = this.$t('SHOP_MANAGEMENT.UPDATE_SUCCESS');
          break;
        case 'create':
          title = this.$t('SHOP_MANAGEMENT.CREATE_SUCCESS');
          break;
        default:
          title = '';
          break;
      }
      this.$refs.checkModal.openModal(title, content);
    },
    closeModalAndLeave() {
      this.$refs.checkModal.closeModal();
      this.$router.push({ name: 'shopManagementDashboard' });
    },
    updateBindingSuccessReplyBody(val) {
      this.currentShopLocation.bindingSuccessReplyBody = val;
    },
    updateBindingSuccessExtraReplyMessage(val) {
      this.bindingSuccessExtraReplyMessage = val;
    },
    async fetchAllBotsLists() {
      try {
        const response = await botApi.getAllChatbots('');
        if (response.status === 200) {
          response.data.forEach((bot) => this.chatbotList.push(
            {
              text: bot.name,
              value: bot.id
            })
          );
        }
      } catch (error) {
        const notificationObj = {
          title: this.$t('ERROR_MSG.FETCH_BOT_LIST_FAILED'),
          status: 'error'
        };
        this.$refs.notification.openNotification(notificationObj);
      }
    },
    checkFormValidate() {
      if (!this.$refs.form.validate()) {
        this.formValid = false;
        const notificationObj = {
          title: this.$t('PLEASE_CHECK_ALL_REQUIRED_FIELDS_ARE_COMPLETED'),
          status: 'error'
        };
        this.$refs.notification.openNotification(notificationObj);
        return false;
      }
      return true;
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep.shop-management-setting {
  &__header {
    z-index: 2 !important;
  }

  .col {
    padding: 10px;
  }

  .row {
    margin: -10px;
  }

  .v-sheet.v-app-bar.v-toolbar:not(.v-sheet--outlined) {
    box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -2px rgba(0, 0, 0, 0.1);;
  }

  &__header {
    padding: 0 50px;

    .v-toolbar__content {
      padding-left: 0;
      padding-right: 0;
    }
  }

  &__form {
    padding: 0 50px;

    &__content {
      width: 475px;
    }
  }

  .cancel-btn {
    .v-btn__content {
      color: $neutral-cold-051;
    }
  }

  .textarea-tool {
    position: relative;
  }

  .v-radio {
    margin-left: 25px;
  }
}
</style>
